import CommonFooter from "../CommonFooter";
import { getGuideLink, User } from "../../services/user/userModel";
import {
  isFreeOrganizationAccount,
  Organization,
} from "../../services/organization/organizationModel";
import { useTranslation } from "react-i18next";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { cx } from "../../react-helpers/css";
import { getPlanLabelKey } from "../../services/subscription/subscriptionModel";
import ProSidebar from "./ProSidebar";
import ProTopbar from "./ProTopbar";
import { CtoRights, useProNav } from "./proNav";
import baseAPI from "../../services/api";
import { useScrollTopOnNavigation } from "./useScrollTopOnNavigation";

// Coquille de l'espace professionnel (menu latéral, barre du haut, pied de page). Deux appelants :
// OrganizationArea (pages /organization/*) et SharedPage (/profile-share/:uid, ouvert par un pro connecté :
// demande de Nicolas du 31/10, garder le menu pendant la lecture d'un profil).
const ProShell = ({
  loggedUser,
  organization,
  children,
}: {
  loggedUser?: User;
  organization: Organization;
  children: ReactNode;
}) => {
  const { t } = useTranslation(["common", "subscription"]);
  const [displayMobileMenu, setDisplayMobileMenu] = useState(false);
  const mainRef = useScrollTopOnNavigation();

  const onClose = useCallback(() => setDisplayMobileMenu(false), []);

  // Free pro account: the sidebar only keeps the "my candidates" and
  // "my account" links, and the header drops the recruiter guide.
  const isFreeAccount = useMemo(
    () => isFreeOrganizationAccount(organization, loggedUser),
    [loggedUser, organization],
  );

  // The backend only returns subscriptions that have not expired yet, so an
  // entry on a Pro+ plan means the section is currently active.
  const hasActiveProPlus = useMemo(
    () =>
      (organization.subscriptions ?? []).some(
        (subscription) => getPlanLabelKey(subscription.plan.type) === "support",
      ),
    [organization.subscriptions],
  );

  // Entrées « Statistiques », « Mon portail » et « Admin 2'MAX » : droits tenus par notre API (même origine,
  // même jeton ; ARCHITECTURE-CIBLE §5.4). En cas d'échec, le menu reste celui de SimplX.
  const [rights, setRights] = useState<CtoRights | null>(null);
  useEffect(() => {
    let alive = true;
    baseAPI
      .get<{ stats: boolean; admin: boolean }>("/auth/capabilities")
      .then(async ({ data }) => {
        const stats = data.stats || data.admin;
        const portal = stats
          ? await baseAPI
              .get<{ enabled: boolean; toComplete: number }>("/portal/summary")
              .then((r) => r.data)
              .catch(() => null)
          : null;
        if (alive)
          setRights({
            stats,
            admin: data.admin,
            portalEnabled: !!portal?.enabled,
            portalToComplete: portal?.toComplete ?? 0,
          });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, []);

  const groups = useProNav({
    isFreeAccount,
    isAdmin: !!loggedUser?.adminOrga,
    hasActiveProPlus,
    rights,
  });

  // Pastille d'offre sous le nom de l'organisation (maquette v3) : lue dans les abonnements en cours,
  // comme le reste de l'écran. Un invité sans abonnement de son organisation n'en a pas.
  const plan = isFreeAccount
    ? { label: t("common:shell.PLAN_FREEMIUM"), variant: "free" as const }
    : hasActiveProPlus
      ? { label: t("subscription:label.support"), variant: "plus" as const }
      : (organization.subscriptions ?? []).length > 0
        ? { label: t("subscription:label.company"), variant: "pro" as const }
        : null;

  const guideLink = isFreeAccount ? null : getGuideLink(loggedUser?.usage);

  return (
    <div className={cx(["shell --pro", displayMobileMenu && "--menu-opened"])}>
      {loggedUser && (
        <ProSidebar
          organizationName={organization.legalName}
          planLabel={plan?.label ?? null}
          planVariant={plan?.variant ?? "pro"}
          groups={groups}
          guideLink={guideLink}
          onNavigate={onClose}
        />
      )}
      {displayMobileMenu && (
        <div className="shell_scrim" onClick={onClose} aria-hidden="true" />
      )}
      <div className="shell_main" ref={mainRef}>
        <ProTopbar
          organizationName={organization.legalName}
          groups={groups}
          guideLink={guideLink}
          menuOpened={displayMobileMenu}
          onOpenMenu={() => setDisplayMobileMenu(true)}
        />
        <main className="shell_content --bg--organization-dashboard">
          {children}
        </main>
        <CommonFooter loggedUser={loggedUser} />
      </div>
    </div>
  );
};

export default ProShell;
